import React, { useState } from 'react';
import { IoMdBulb } from 'react-icons/io';
import { generateAIMessage } from '../store/features/ownerActions';
import { useDispatch, useSelector } from "react-redux";
import { RootState, templateSelector } from "../store/store";
import { setSelectedTemplate } from '../store/slices/templateSlice';
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { BsMagic } from 'react-icons/bs';


const AIInputSection = () => {

  const { selectedTemplate, templates } = useSelector(templateSelector);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiInputs, setAiInputs] = useState({
    target: "",
    valueProposition: "",
    differentiatingFactor: "",
    callToAction: ""
  });
  const [loading, setLoading] = useState(false);
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);


    
  const handleAiInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAiInputs({
      ...aiInputs,
      [e.target.name]: e.target.value
    });
  };

  const handleGenerateWithDetails = async () => {
    setLoading(true);
    try {
      // Call your AI generation API with the inputs
      const res = await dispatch(generateAIMessage(aiInputs));
      if (res.meta.requestStatus === "fulfilled") {
        const payload = res.payload;
        dispatch(
          setSelectedTemplate({
            ...selectedTemplate,
            content: {
              ...selectedTemplate.content,
              body: payload,
            },
          })
        );
        setAiModalOpen(false);
      }
    } catch (error) {
      toast.error("Error generating AI message");
    } finally {
      setLoading(false);
    }
  };

  const toggleAccordion = () => {
    setIsAccordionOpen(!isAccordionOpen);
  };

  return (
    <div className="w-[100%] gap-2 flex flex-col px-4 border-b-2 border-gray-200 pb-4">
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-lg border-2 border-blue-100 p-3">
        <button
          onClick={toggleAccordion}
          className="flex justify-between items-center w-full focus:outline-none group"
        >
          <div className="border-2 flex w-full justify-start gap-2 font-semibold items-center text-white bg-gradient-to-r from-blue-600 to-purple-700 p-3 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 group-hover:scale-[1.02]">
            <div className="p-2 bg-white/20 rounded-full">
              <BsMagic size={30} className="text-yellow-300" />
            </div>
            <span className="text-lg">Lumina, your AI assistant</span>
            <svg
              className={`w-6 h-6 text-yellow-300 transform transition-transform duration-300 ${
                isAccordionOpen ? 'rotate-180' : ''
              }`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </button>

        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isAccordionOpen ? 'max-h-[1000px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <p className="text-gray-600 mb-4 text-sm font-medium">Craft the perfect message with AI assistance</p>
          
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">Generating your message...</p>
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto"></div>
            </div>
          ) : (
            <div className="mt-4">
              <div className="space-y-2">
                <div>
                  <label className="block text-sm font-semibold mb-1.5 text-gray-800 tracking-tight">Target</label>
                  <input
                    type="text"
                    name="target"
                    value={aiInputs.target}
                    onChange={handleAiInputChange}
                    placeholder="e.g: Business manager"
                    className="w-full p-3 border-2 border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-blue-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5 text-gray-800 tracking-tight">Value proposition</label>
                  <input
                    type="text"
                    name="valueProposition"
                    value={aiInputs.valueProposition}
                    onChange={handleAiInputChange}
                    placeholder="I offer..."
                    className="w-full p-3 border-2 border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-blue-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5 text-gray-800 tracking-tight">Differentiating factor</label>
                  <input
                    type="text"
                    name="differentiatingFactor"
                    value={aiInputs.differentiatingFactor}
                    onChange={handleAiInputChange}
                    placeholder="I stand out by..."
                    className="w-full p-3 border-2 border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-blue-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5 text-gray-800 tracking-tight">Call to action</label>
                  <input
                    type="text"
                    name="callToAction"
                    value={aiInputs.callToAction}
                    onChange={handleAiInputChange}
                    placeholder="I would like my prospects to..."
                    className="w-full p-3 border-2 border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-blue-200"
                  />
                </div>
              </div>
              <button
                onClick={handleGenerateWithDetails}
                className="mt-6 w-full bg-gradient-to-r from-blue-600 to-purple-700 text-white py-3 px-4 rounded-xl hover:opacity-90 transition-all transform hover:scale-[1.02] active:scale-95 shadow-md font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate Message
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIInputSection; 